import { PhoneNumberUtil } from 'google-libphonenumber';
import moment from 'moment';
import { IntegrationApp } from 'queries';

import { NodeEnvironment } from 'utils/enum';

export const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // https://stackoverflow.com/a/9204568
export const datePickerFormat = 'MM/dd/yyyy';
const phoneUtil = PhoneNumberUtil.getInstance();

export function capitalizeFirstLetter(value: string): string {
  if (!value) {
    return '';
  }
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export function isValidPhone(phone: string): boolean {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
}

export function isValidEmail(email: string): boolean {
  return emailRegExp.test(email);
}

const removeHTMLTags = (str: string) => {
  if (!str) {
    return str;
  }

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  // https://www.geeksforgeeks.org/how-to-strip-out-html-tags-from-a-string-using-javascript/
  return str.replace(/(<([^>]+)>)/gi, '');
};

export function truncateText(text: string, length: number, endText = '...'): string {
  if (!text) {
    return '';
  }
  const sanitizedText = removeHTMLTags(text);
  if (sanitizedText.length <= length) {
    return sanitizedText;
  }
  return sanitizedText.substring(0, length) + endText;
}

export function centsToDollarAmount(cents: number): string {
  if (cents === 0) {
    return '0';
  }
  return (cents / 100).toFixed(2).toString();
}

export function upperCaseFirst(label: string): string {
  return `${label.slice(0, 1).toUpperCase()}${label.slice(1).toLowerCase()}`;
}

export function getNumberOfDaysBetweenDates(startDate: Date, endDate: Date): number {
  if (!startDate || !endDate) {
    return -1;
  }

  // https://stackoverflow.com/a/9130040
  const startDateMoment = moment(startDate).startOf('day');
  const endDateMoment = moment(endDate).startOf('day');

  const days = Math.abs(endDateMoment.diff(startDateMoment, 'days'));

  return days;
}

/**
 * Formats the currency value returned by Stripe into a currency value.
 * @param amount Amount of currency as a whole number as a string (1550)
 * @param truncateCents Boolean to remove empty cents values (1500 -> 15)
 * @returns String value as formatted currency (1550 -> $15.50)
 */
export function formatCurrency(amount: string, truncateCents?: boolean): string {
  const numberAmount = parseInt(amount, 10) / 100;
  let currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    numberAmount,
  );
  if (truncateCents) {
    currency = currency.slice(0, -3);
  }
  return currency;
}

/**
 * Method that reverses the formatted currency method. Stripe communicates currency in whole numbers, no decimals
 * @param formattedAmount - A string formatted as currency ($15.50)
 * @returns - whole number format (1550)
 */
export function reverseFormatCurrency(formattedAmount: string): number {
  const numericString = formattedAmount.replace(/[^0-9.]/g, '');
  const numberAmount = parseFloat(numericString) * 100;
  return numberAmount;
}

export function percentFromString(percentAsString: string): number {
  const percent = Number(percentAsString) / 100;
  return percent;
}

/**
 * Remove characters that when converted to UTF8 are causing DB insertion errors.
 * @param key
 * @param value
 * @returns string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sanitizeUTF8 = (_: string | null = null, value: any): string => {
  if (typeof value === 'string') {
    // 0xd7 (multiplication sign)
    value = value.replace(/(?:\u00d7|&#125;|&times;|0x[dD]7)/g, '×');
    // 0x20 (space)
    value = value.replace(/(?:&#32;|&nbsp;|&#160;|0x20|\u0020)/g, ' ');
    // 0xA0 (0xa0) & nbsp; UTF8 encodings are causing DB insertion errors.
    value = value.replace(/(?:\u00A0|&(?:nbsp|#160|amp;nbsp);|0x[aA])/g, ' ');
  }
  return value;
};

export const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return value ? 'Yes' : 'No';
};

export const formatEnum = (text: string | null | undefined): string => {
  if (!text) {
    return '';
  }
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

export const getIntegrationAppLabel = (app: string | null) => {
  switch (app) {
    case IntegrationApp.DecisionVault:
      return 'DecisionVault';
    default:
      return app || 'unknown service';
  }
};

export const getAppIntegrationAuthUrl = (app: IntegrationApp, portalId: string) => {
  switch (app) {
    case IntegrationApp.DecisionVault:
      const clientId = process.env.REACT_APP_DECISION_VAULT_CLIENT_ID;
      const redirectUri = `${getDecisionVaultOAuthRedirectUrl()}?app=${app}`;
      return `${getDecisionVaultApiOAuthUrlForNodeEnvironment()}/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${portalId}`;
  }
};

const getDecisionVaultApiOAuthUrlForNodeEnvironment = (): string => {
  if (process.env.REACT_APP_NODE_ENV !== NodeEnvironment.PROD) {
    return 'https://staging.decisionvault.com';
  } else {
    return 'https://app.decisionvault.com';
  }
};

const getDecisionVaultOAuthRedirectUrl = (): string => {
  if (process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV) {
    return 'http://localhost:3000/home/auth-result';
  } else if (process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT) {
    return 'https://uat.trustate.com/home/auth-result';
  } else {
    return 'https://app.trustate.com/home/auth-result';
  }
};

export const replaceUnderscoresWithSpace = (value: string): string => {
  return value.replace(/_/g, ' ');
};
